import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  useTheme,
  Skeleton,
  Typography,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Divider,
  alpha
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Warehouse,
  Inventory2,
  TrendingUp
} from '@mui/icons-material';

interface StorageDistribution {
  storageUnitId: string;
  storageUnitCode: string;
  storageUnitName: string;
  warehouseId: string;
  warehouseName: string;
  warehouseCode: string;
  quantity: number;
}

interface InventoryBreakdownItem {
  value: string;
  quantity: number;
  storageDistribution: StorageDistribution[];
}

interface InventoryByProperty {
  propertyName: string;
  breakdown: InventoryBreakdownItem[];
}

interface InventoryStorageDistributionProps {
  data: InventoryByProperty | null;
  isLoading: boolean;
  error: Error | null;
  colors: string[];
}

const COLORS = [
  '#4F8DFD',
  '#22c55e',
  '#f59e42',
  '#ef4444',
  '#38bdf8',
  '#818cf8',
  '#f472b6',
  '#22d3ee'
];

export const InventoryStorageDistribution: React.FC<InventoryStorageDistributionProps> = ({
  data,
  isLoading,
  error,
  colors = COLORS
}) => {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleRow = (key: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Calculate total and statistics
  const totalQuantity = data?.breakdown.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const totalLocations = data?.breakdown.reduce(
    (sum, item) => sum + (item.storageDistribution?.length || 0),
    0
  ) || 0;
  const uniqueWarehouses = data
    ? new Set(
        data.breakdown.flatMap((item) =>
          item.storageDistribution.map((s) => s.warehouseId)
        )
      ).size
    : 0;

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: theme.general.borderRadius,
        boxShadow: theme.colors.shadows.card
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Inventory2 />
            <Typography variant="h4" fontWeight={700}>
              Storage Distribution
            </Typography>
          </Box>
        }
        subheader={
          data && (
            <Typography variant="body2" color="text.secondary">
              {data.propertyName} breakdown by storage location
            </Typography>
          )
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from(new Array(5)).map((_, index) => (
              <Skeleton key={index} variant="rectangular" height={60} />
            ))}
          </Box>
        ) : error ? (
          <Typography color="error" textAlign="center">
            Failed to load storage distribution
          </Typography>
        ) : data && data.breakdown && Array.isArray(data.breakdown) && data.breakdown.length > 0 ? (
          <>
            {/* Summary Statistics */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 2,
                mb: 3,
                p: 2,
                borderRadius: theme.general.borderRadiusSm,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={700} color="primary">
                  {totalQuantity.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Units
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={700} color="primary">
                  {totalLocations}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Storage Locations
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={700} color="primary">
                  {uniqueWarehouses}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Warehouses
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Distribution Table */}
            <Box>
              {data.breakdown.map((valueItem, index) => {
                const rowKey = `${data.propertyName}-${valueItem.value}`;
                const isExpanded = expandedRows[rowKey];
                const color = colors[index % colors.length];
                const percentage = totalQuantity > 0 ? (valueItem.quantity / totalQuantity) * 100 : 0;

                return (
                  <Box
                    key={valueItem.value}
                    sx={{
                      mb: 2,
                      borderRadius: theme.general.borderRadiusSm,
                      border: `1px solid ${theme.colors.alpha.black[10]}`,
                      overflow: 'hidden',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: theme.colors.shadows.cardSm,
                        borderColor: color
                      }
                    }}
                  >
                    {/* Main Row */}
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: alpha(color, 0.05),
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}
                      onClick={() => toggleRow(rowKey)}
                    >
                      <IconButton size="small" sx={{ color: color }}>
                        {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>

                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '4px',
                          backgroundColor: color,
                          boxShadow: `0 0 8px ${alpha(color, 0.4)}`
                        }}
                      />

                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {valueItem.value}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip
                            label={`${valueItem.quantity.toLocaleString()} units`}
                            size="small"
                            sx={{
                              backgroundColor: color,
                              color: '#fff',
                              fontWeight: 600,
                              height: 20
                            }}
                          />
                          <Chip
                            label={`${percentage.toFixed(1)}%`}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: color,
                              color: color,
                              height: 20
                            }}
                          />
                          <Chip
                            icon={<Warehouse sx={{ fontSize: 14 }} />}
                            label={`${valueItem.storageDistribution?.length || 0} locations`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20 }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ width: 150 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(percentage, 100)}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha(color, 0.1),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: color,
                              borderRadius: 4
                            }
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          {percentage.toFixed(1)}% of total
                        </Typography>
                      </Box>
                    </Box>

                    {/* Expanded Details */}
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, backgroundColor: theme.colors.alpha.black[5] }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <TrendingUp sx={{ color: color, fontSize: 20 }} />
                          <Typography variant="subtitle2" fontWeight={600}>
                            Storage Locations for {valueItem.value}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {valueItem.storageDistribution.map((storage, storageIndex) => {
                            const storagePercentage = valueItem.quantity > 0
                              ? (storage.quantity / valueItem.quantity) * 100
                              : 0;

                            return (
                              <Box
                                key={storage.storageUnitId}
                                sx={{
                                  p: 1.5,
                                  borderRadius: theme.general.borderRadiusSm,
                                  backgroundColor: theme.palette.background.paper,
                                  border: `1px solid ${theme.colors.alpha.black[10]}`,
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    backgroundColor: alpha(color, 0.05),
                                    borderColor: alpha(color, 0.3),
                                    transform: 'translateX(4px)'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      <Inventory2 sx={{ fontSize: 16, color: color }} />
                                      <Typography variant="body2" fontWeight={600}>
                                        {storage.storageUnitName}
                                      </Typography>
                                      <Chip
                                        label={storage.storageUnitCode}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                      />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Warehouse sx={{ fontSize: 14, color: 'text.secondary' }} />
                                      <Typography variant="caption" color="text.secondary">
                                        {storage.warehouseName}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        ({storage.warehouseCode})
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="h6" fontWeight={700} color={color}>
                                      {storage.quantity.toLocaleString()}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {storagePercentage.toFixed(1)}%
                                    </Typography>
                                  </Box>
                                </Box>

                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(storagePercentage, 100)}
                                  sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: alpha(color, 0.1),
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: color,
                                      borderRadius: 3
                                    }
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          </>
        ) : (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 3 }}>
            No storage distribution data available
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
